/** Instagram Login (ALP-77): connect explainer, OAuth return toasts, token + webhook status, initial import progress. */
export const oauth = {
  explainer: {
    title: "Connect Instagram",
    intro: "You'll continue on Instagram to choose the account and approve these permissions. Malfak never sees your password.",
    permissionsTitle: "What Malfak asks for, and why",
    permissions: {
      instagram_business_basic: { title: "Profile and posts", why: "Your account name, picture, followers and your posts and reels, so they appear in Malfak." },
      instagram_business_manage_comments: { title: "Comments", why: "Read, reply to, hide and delete comments on your posts from the inbox and automations." },
      instagram_business_manage_messages: { title: "Direct messages", why: "Read and answer DMs. Instagram only allows replies within 24 hours of the customer's last message." },
      instagram_business_content_publish: { title: "Publishing", why: "Publish the posts, reels and stories you schedule in Malfak." },
      instagram_business_manage_insights: { title: "Insights", why: "Reach, views and engagement for your dashboards and reports." },
    },
    importNote: "After you approve, Malfak imports your posts, comments and messages in the background, newest first. You can keep working while it runs.",
    neverPosts: "Malfak never posts, replies or messages on your behalf unless you or a rule you set up asks it to.",
    accountsNote: "Instagram lets you pick or switch the account, so you can connect several accounts one after another.",
    requirement: "Requires an Instagram business or creator account.",
    continue: "Continue to Instagram",
    cancel: "Not now",
    privacy: "Privacy policy",
  },
  returned: {
    connected: "@{{user}} is connected",
    connectedGeneric: "Instagram is connected",
    connectedHint: "We're importing your history in the background. New posts and comments appear as they arrive.",
    failedTitle: "Instagram wasn't connected",
    errors: {
      access_denied: "You cancelled on Instagram, so nothing was connected. You can try again any time.",
      state_invalid: "The sign-in link was not valid. Please start again from Malfak.",
      state_expired: "The sign-in took too long and expired. Please start again.",
      state_reused: "That sign-in link was already used. Please start again from Malfak.",
      forbidden: "Only organization owners and admins can connect accounts. Ask an admin to connect it.",
      exchange_failed: "Instagram didn't confirm the sign-in. Please try again in a minute.",
      profile_failed: "We couldn't read the account's profile from Instagram. Please try again.",
      not_professional: "This Instagram account is a personal account. Switch it to Professional (Business or Creator) in Instagram settings, then connect again.",
      not_tester: "This Instagram account isn't an Instagram tester of the app yet. Add it under App roles → Instagram testers in the Meta dashboard and accept the invite in Instagram (Settings → Website permissions → Tester invites), then try again.",
      account_conflict: "This Instagram account is already connected to another organization. Disconnect it there first.",
      not_configured: "Instagram Login isn't configured on this server yet. Ask your administrator.",
      server_error: "Something went wrong on our side. Please try again.",
      unknown: "Instagram wasn't connected ({{code}}). Please try again.",
    },
  },
  token: {
    label: "Access",
    expires: "Renews automatically · expires {{when}}",
    soon: "Expires {{when}}: reconnect if renewal fails",
    expired: "Expired {{when}}: reconnect to continue",
    unknown: "No expiry reported",
  },
  webhook: {
    since: "Subscribed {{when}}",
    failed: "Subscription failed: {{error}}",
    failedGeneric: "Subscription failed. Reconnect to retry.",
    /** Meta webhook field codes as words (features/settings/webhook-fields.ts). */
    fields: {
      comments: "Comments",
      live_comments: "Live video comments",
      mentions: "Mentions",
      messages: "Direct messages",
      message_edit: "Edited messages",
      message_reactions: "Message reactions",
      messaging_postbacks: "Button taps in DMs",
      messaging_seen: "Read receipts",
      messaging_referral: "Ad and link referrals",
      messaging_optins: "Message opt-ins",
      messaging_handover: "Conversation handovers",
      story_insights: "Story insights",
    },
  },
  reauth: {
    title: "Reconnect needed",
    body: "Instagram stopped accepting Malfak's access for @{{user}} (expired or removed). Reconnect to resume comments, DMs and publishing.",
    needed: "Instagram needs to be reconnected",
    hint: "Instagram no longer accepts Malfak's access for this account, so nothing was sent. Reconnect it, then try again.",
    hintUser: "Instagram no longer accepts Malfak's access for @{{user}}, so nothing was sent. Reconnect it, then try again.",
    reconnect: "Reconnect",
  },
  backfill: {
    title: "Importing @{{user}}",
    titleGeneric: "Importing history",
    hint: "Newest first. Everything imported so far is already in your inbox, posts and dashboards.",
    line: {
      posts: "Posts {{done}}",
      postsOf: "Posts {{done}}/{{total}}",
      comments: "Comments {{done}}",
      messages: "Messages {{done}}",
    },
    stage: {
      profile: "Reading profile…",
      insights: "Reading insights…",
      media: "Importing posts and comments…",
      messages: "Importing messages…",
      done: "Import complete",
    },
    rateLimited_one: "Paused briefly by Instagram's rate limit, {{count}} time",
    rateLimited_other: "Paused briefly by Instagram's rate limit, {{count}} times",
    noAutomation: "Imported history never triggers automations or auto-replies.",
    // QA F8: honest partial result
    missing: {
      short: "{{n}} comments not fetched",
      title: "Partial import: {{n}} comments not fetched",
      not_returned: "{{n}} not returned by Instagram. Instagram counts them, but its API doesn't list them (for example comments from restricted accounts or held for review).",
      api_error: "{{n}} could not be read: Instagram API error{{detail}}.",
      recheck: "Malfak checks these posts again automatically and adds anything Instagram returns later.",
      posts: "{{n}} posts affected",
    },
  },
};
